import { reactive, ref, computed } from 'vue'
import * as authApi from '../api/auth'
import { getTenantIdFromToken, getUserIdFromToken } from '../utils/jwt'
import type { components } from '@helmetcare/api-client'

interface AuthState {
  accessToken: string | null
  user: components['schemas']['UserResponse'] | null
}

const REFRESH_TOKEN_KEY = 'hc.refreshToken'

class AuthStore {
  private state = reactive<AuthState>({
    accessToken: null,
    user: null,
  })

  refreshToken = ref<string | null>(null)
  isLoading = ref(false)
  error = ref<string | null>(null)
  accessToken = ref<string | null>(null)
  user = ref<components['schemas']['UserResponse'] | null>(null)

  tenantId = computed(() => getTenantIdFromToken(this.accessToken.value))
  userId = computed(() => getUserIdFromToken(this.accessToken.value))

  constructor() {
    this.loadRefreshToken()
  }

  private loadRefreshToken() {
    const stored = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (stored) {
      this.refreshToken.value = stored
    }
  }

  private saveRefreshToken() {
    if (this.refreshToken.value) {
      localStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken.value)
    }
  }

  async login(
    email: string,
    password: string,
  ): Promise<
    | { mfaRequired: true; sessionId: string; expiresIn: number }
    | { mfaRequired: false; user: components['schemas']['UserResponse'] }
  > {
    this.isLoading.value = true
    this.error.value = null

    try {
      const response = await authApi.login(email, password)

      if (authApi.isMfaRequired(response)) {
        return {
          mfaRequired: true,
          sessionId: response.sessionId,
          expiresIn: response.mfaChallenge.expiresIn,
        }
      }

      if (authApi.isLoginSuccess(response)) {
        this.setTokens(response.accessToken, response.refreshToken)
        this.setUser(response.user)
        return {
          mfaRequired: false,
          user: response.user,
        }
      }

      throw new Error('Invalid response format')
    } catch (err: unknown) {
      const error = err as { message?: string; code?: string } | undefined
      const message = error?.message ?? '登录失败'
      const code = error?.code
      let displayMessage = message

      // Map error codes to Chinese messages
      if (code === 'AUTH_FAILED') {
        displayMessage = '邮箱或密码错误'
      } else if (code === 'RATE_LIMITED') {
        displayMessage = '尝试过多请稍后再试'
      } else if (code === 'INVALID_PARAMETER') {
        displayMessage = '参数错误'
      }

      this.error.value = displayMessage
      throw err
    } finally {
      this.isLoading.value = false
    }
  }

  async verifyMfa(sessionId: string, totpCode: string) {
    this.isLoading.value = true
    this.error.value = null

    try {
      const response = await authApi.verifyMfa(sessionId, totpCode)
      this.setTokens(response.accessToken, response.refreshToken)
      return response
    } catch (err: unknown) {
      const error = err as { message?: string; code?: string } | undefined
      const message = error?.message ?? 'MFA 验证失败'
      const code = error?.code
      let displayMessage = message

      if (code === 'AUTH_FAILED') {
        displayMessage = '验证码错误或已过期'
      } else if (code === 'RATE_LIMITED') {
        displayMessage = '尝试过多请稍后再试'
      }

      this.error.value = displayMessage
      throw err
    } finally {
      this.isLoading.value = false
    }
  }

  async logout() {
    try {
      await authApi.logout()
    } catch (err) {
      // Silently fail on logout to still clear local state
      console.error('Logout error:', err)
    }

    this.clearAuth()
  }

  async tryRefresh() {
    if (!this.refreshToken.value) {
      return false
    }

    try {
      const response = await authApi.refresh(this.refreshToken.value)
      this.setTokens(response.accessToken, response.refreshToken)
      return true
    } catch {
      this.clearAuth()
      return false
    }
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken.value = accessToken
    this.refreshToken.value = refreshToken
    this.saveRefreshToken()
  }

  setUser(user: components['schemas']['UserResponse']) {
    this.user.value = user
  }

  clearAuth() {
    this.accessToken.value = null
    this.refreshToken.value = null
    this.user.value = null
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    this.error.value = null
  }

  isAuthenticated(): boolean {
    return !!this.accessToken.value
  }
}

export default new AuthStore()
